import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useLanguage } from '../hooks/useLanguage'
import type { Language } from './SiteHeader'

type LanguageValue = {
  language: Language
  setLanguage: (next: Language) => void
}

const LanguageContext = createContext<LanguageValue | null>(null)

/**
 * Stellt die aktive Sprache für die ganze Seite bereit. Die Wahl wird im
 * localStorage gemerkt und auf `<html lang>` gespiegelt (siehe `useLanguage`).
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useLanguage()
  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

const FALLBACK: LanguageValue = { language: 'de', setLanguage: () => {} }

/**
 * Aktive Sprache. Ohne umgebenden Provider wird Deutsch angenommen, damit
 * einzelne Bauteile auch isoliert (z. B. in Tests) verwendbar bleiben.
 */
export function useLanguageValue(): LanguageValue {
  return useContext(LanguageContext) ?? FALLBACK
}

/**
 * Wählt aus einem Wörterbuch die Fassung in der aktiven Sprache.
 * Wird in jeder App als `useContent()` weiterverwendet.
 */
export function usePick<T>(dictionary: Record<Language, T>): T {
  const { language } = useLanguageValue()
  return dictionary[language]
}
